import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import type { TagGroup } from "@models/tagGroup.model";
import { fuzzyMatch } from "@/shared/utils/fuzzyMatch";

/*
    Groups smaller than this need no search box -- Design Type has three options and an input
    above them is just clutter. Matches the threshold the Content Catalog already uses.
*/
const SEARCHABLE_FROM = 5;

interface StoreFilterBarProps {
    tagGroups: TagGroup[];
    selectedTagIds: number[];
    onToggleTag: (tagId: number) => void;
    onClearTags: () => void;
    query: string;
    onQueryChange: (value: string) => void;
    resultCount: number;
    isFiltering: boolean;
}

/**
 * Search and tag filtering for a Moti Store collection page (MPN-1575).
 *
 * The groups come from the same tag-groups endpoint the Content Catalog reads, so a group
 * added there is filterable here with no change on this side. That is the point of the AC
 * asking for the full taxonomy rather than a curated subset: the store's home page tag
 * sections stay curated by Store Settings, but the filter must not be.
 */
export default function StoreFilterBar({
    tagGroups,
    selectedTagIds,
    onToggleTag,
    onClearTags,
    query,
    onQueryChange,
    resultCount,
    isFiltering,
}: StoreFilterBarProps) {
    const [openGroupId, setOpenGroupId] = useState<number | null>(null);
    const [tagQuery, setTagQuery] = useState("");
    const tagSearchRef = useRef<HTMLInputElement | null>(null);

    // Focus the search as the menu opens, so a long group is one click and then typing
    // rather than click, aim, click again. Target Action carries 191 tags; scrolling that
    // is not a way to find anything.
    useEffect(() => {
        setTagQuery("");
        if (openGroupId != null) tagSearchRef.current?.focus();
    }, [openGroupId]);

    // No early return when the taxonomy is empty. The groups and the search box fail
    // independently, and losing the tag list should not cost the client search as well.

    return (
        <div className="store-filter">
            <div className="store-filter__row">
                <div className="store-filter__search">
                    <Search size={14} />
                    <input
                        type="text"
                        placeholder="Search this collection"
                        aria-label="Search this collection"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                    />
                    {query && (
                        <button
                            type="button"
                            aria-label="Clear search"
                            onClick={() => onQueryChange("")}
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>

                {tagGroups.map((group) => {
                    const activeCount = group.tags.filter((t) => selectedTagIds.includes(t.id)).length;
                    const isOpen = openGroupId === group.id;
                    return (
                        <div key={group.id} className="store-filter__group">
                            <button
                                type="button"
                                className={`store-filter__group-button${activeCount > 0 ? " active" : ""}`}
                                aria-expanded={isOpen}
                                onClick={() => setOpenGroupId(isOpen ? null : group.id)}
                            >
                                {group.name}
                                {activeCount > 0 && (
                                    <span className="store-filter__badge">{activeCount}</span>
                                )}
                                <ChevronDown size={13} />
                            </button>
                            {isOpen && (
                                <div className="store-filter__menu">
                                    {group.tags.length >= SEARCHABLE_FROM && (
                                        <div className="store-filter__tag-search">
                                            <Search size={12} />
                                            <input
                                                ref={tagSearchRef}
                                                type="text"
                                                placeholder={`Filter ${group.name}`}
                                                aria-label={`Filter ${group.name}`}
                                                value={tagQuery}
                                                onChange={(e) => setTagQuery(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Escape") setOpenGroupId(null);
                                                }}
                                            />
                                        </div>
                                    )}
                                    {group.tags.length === 0 ? (
                                        <p className="store-filter__no-tags">No tags in this group.</p>
                                    ) : (
                                        (() => {
                                            // Selected tags always stay listed, even when the
                                            // query excludes them -- otherwise typing makes a
                                            // choice you already made look like it was undone.
                                            const matching = group.tags.filter(
                                                (t) =>
                                                    selectedTagIds.includes(t.id) ||
                                                    fuzzyMatch(t.name, tagQuery),
                                            );
                                            if (matching.length === 0) {
                                                return <p className="store-filter__no-tags">No matches.</p>;
                                            }
                                            return matching.map((tag) => (
                                                <label key={tag.id} className="store-filter__tag">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTagIds.includes(tag.id)}
                                                        onChange={() => onToggleTag(tag.id)}
                                                    />
                                                    {tag.name}
                                                </label>
                                            ));
                                        })()
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {selectedTagIds.length > 0 && (
                    <button type="button" className="store-filter__clear" onClick={onClearTags}>
                        Clear tags ({selectedTagIds.length})
                    </button>
                )}
            </div>

            {isFiltering && (
                <div className="store-filter__count">
                    {resultCount} {resultCount === 1 ? "GuidePost" : "GuidePosts"}
                </div>
            )}
        </div>
    );
}
